// The people list behind "New chat" and "New group": what the server offers for the
// words in the find box, the way the app's two pickers ask (apps/mobile/src/screens/
// social/DmDirectoryScreen.tsx and DmGroupCreateScreen.tsx).
//
// Nothing typed, or fewer than two letters: the person's own team. Two letters or
// more: a search of the whole project. Both are the server's answer; this only asks.
// The first list is asked for at once, and each change of words after it waits 300 ms
// for the typing to stop. An answer for words that have since been typed past is
// dropped, so an older, slower answer never replaces a newer one.
import { useCallback, useEffect, useRef, useState } from 'react';
import { MIN_FIND_CHARS, type PickPerson } from '../../services/messagesApi';

/** How long the find box waits for the typing to stop before it asks. The app's wait. */
export const FIND_WAIT_MS = 300;

/**
 * Why there is no list. 'off' is the server's plain refusal (404, or 403 for a sign-in
 * it issued itself): not for this person here. 'unconfirmed' is a 401: the server
 * could not name them. 'failed' is anything else, worth trying again.
 */
export type FindFailure = 'off' | 'unconfirmed' | 'failed';

type Ask = (projectId: number, q: string, signal?: AbortSignal) => Promise<{ data: { data?: PickPerson[] } }>;

interface Found {
  people: PickPerson[];
  /** A list is on its way. The one on screen stays until it arrives. */
  loading: boolean;
  failure: FindFailure | null;
  /** The list on screen is a search of the project, not the team. */
  searching: boolean;
  retry: () => void;
}

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

export const failureOf = (err: unknown): FindFailure => {
  const status = statusOf(err);
  if (status === 404 || status === 403) return 'off';
  return status === 401 ? 'unconfirmed' : 'failed';
};

/** The words the server is asked with: empty for the team, else the trimmed words. */
export const findWords = (typed: string): string => {
  const words = typed.trim();
  return words.length >= MIN_FIND_CHARS ? words : '';
};

/** The grey line under a name: the title and the store, or the code when there is neither. As the app. */
export const pickLine = (person: PickPerson): string =>
  [person.designation, person.storeName].filter(Boolean).join(' · ') || person.empCode;

export function usePeopleFinder(projectId: number, typed: string, ask: Ask): Found {
  const wanted = findWords(typed);
  // `words` is what the list on screen was asked with: a search still on its way does
  // not turn the team on screen into "results".
  const [found, setFound] = useState<{ people: PickPerson[]; words: string; loading: boolean; failure: FindFailure | null }>({
    people: [],
    words: '',
    loading: true,
    failure: null,
  });
  const [again, setAgain] = useState(0);
  // Counts the askings. An answer that comes back under an older count is dropped.
  const round = useRef(0);
  // The first asking (and a Try again) goes at once; a change of words waits.
  const atOnce = useRef(true);
  // The call as it is now, so a caller's new function does not ask again by itself.
  const asking = useRef(ask);
  useEffect(() => { asking.current = ask; }, [ask]);

  useEffect(() => {
    const id = ++round.current;
    const controller = new AbortController();
    const go = () => {
      setFound((was) => ({ ...was, loading: true }));
      Promise.resolve()
        .then(() => asking.current(projectId, wanted, controller.signal))
        .then(
          (res) => {
            if (id !== round.current) return;
            setFound({ people: Array.isArray(res?.data?.data) ? res.data.data : [], words: wanted, loading: false, failure: null });
          },
          (err) => {
            if (id !== round.current) return;
            setFound({ people: [], words: wanted, loading: false, failure: failureOf(err) });
          },
        );
    };
    let wait: ReturnType<typeof setTimeout> | null = null;
    if (atOnce.current) {
      atOnce.current = false;
      go();
    } else {
      wait = setTimeout(go, FIND_WAIT_MS);
    }
    return () => {
      // Whatever this asking still has on its way is no longer wanted.
      round.current += 1;
      if (wait) clearTimeout(wait);
      controller.abort();
    };
  }, [projectId, wanted, again]);

  const retry = useCallback(() => {
    atOnce.current = true;
    setAgain((n) => n + 1);
  }, []);

  const { people, words, loading, failure } = found;
  return { people, loading, failure, searching: words !== '', retry };
}
